import { Check, Copy, Link2, Mail, Pencil, Plus, Send, Trash2, Wallet, type LucideIcon } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError, erpApi, type BalloonAddonOption } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useErpData } from "../data/DataProvider";
import type { Booking, Confirmation } from "../data/types";
import { mexicoDateKey } from "../data/format";
import { DateBox } from "./DateBox";
import { Button, Dialog, Field, InlineNotice, SelectField, type Tone } from "./ui";
import type { PaymentMethod } from "../data/payment-method";

type ActionKind = "email" | "edit" | "addon" | "share" | "delete" | "payment" | null;
const actionTitles = { payment: "Registrar pago", email: "Enviar confirmación", edit: "Editar reserva", addon: "Agregar producto pagado", share: "Compartir por WhatsApp", delete: "Eliminar permanentemente" } as const;

export function BookingActions({ booking, confirmation, paymentSummary, onlyPayment = false, payRequest = 0, hide = [] }: { booking: Booking; confirmation?: Confirmation | undefined; paymentSummary?: unknown; /** Side panel: just "Registrar pago". */ onlyPayment?: boolean; /** Each increase opens "Registrar pago" (the phone's bottom bar asks for it). */ payRequest?: number; /** Actions the screen already offers elsewhere, or does not need. */ hide?: Array<"edit" | "addon"> }) {
  const { hasPermission } = useAuth();
  const { executeCommand } = useErpData();
  const [action, setAction] = useState<ActionKind>(null);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackTone, setFeedbackTone] = useState<Tone>("success");
  const [customer, setCustomer] = useState(booking.customer);
  const [email, setEmail] = useState(booking.email);
  const [phone, setPhone] = useState(booking.phone);
  const [language, setLanguage] = useState<"es" | "en">(booking.language);
  const [passengers, setPassengers] = useState(() => passengerDrafts(booking));
  const [addonName, setAddonName] = useState("");
  const [addonKey, setAddonKey] = useState("");
  const [addonCurrency, setAddonCurrency] = useState("MXN");
  const [addonCatalog, setAddonCatalog] = useState<BalloonAddonOption[]>([]);
  const [addonCatalogLoading, setAddonCatalogLoading] = useState(false);
  const [addonCatalogError, setAddonCatalogError] = useState("");
  const [addonQuantity, setAddonQuantity] = useState("1");
  const [addonPrice, setAddonPrice] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>("cash");
  const [payKind, setPayKind] = useState<"payment" | "reservation_fee">("payment");
  const [payDate, setPayDate] = useState(() => mexicoDateKey());
  const [payNote, setPayNote] = useState("");
  const [deleteReference, setDeleteReference] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const canConfirm = booking.paid && ["confirmed", "completed"].includes(booking.status);

  useEffect(() => {
    setCustomer(booking.customer);
    setEmail(booking.email);
    setPhone(booking.phone);
    setLanguage(booking.language);
    setPassengers(passengerDrafts(booking));
  }, [booking]);

  useEffect(() => {
    if (action !== "addon" || addonCatalog.length > 0) return;
    let active = true;
    setAddonCatalogLoading(true);
    setAddonCatalogError("");
    void erpApi.balloonAddons()
      .then((items) => {
        if (active) setAddonCatalog(items);
      })
      .catch((caught) => {
        if (active) setAddonCatalogError(caught instanceof Error ? caught.message : "No fue posible cargar los productos vigentes.");
      })
      .finally(() => {
        if (active) setAddonCatalogLoading(false);
      });
    return () => { active = false; };
  }, [action, addonCatalog.length]);

  const summary = (paymentSummary && typeof paymentSummary === "object" ? paymentSummary : {}) as Record<string, unknown>;
  const fee = Number(summary.reservation_fee) || 0;
  const feePaid = Number(summary.reservation_fee_paid) || 0;
  const balance = Number(summary.balance_due) || 0;
  const open = (next: Exclude<ActionKind, null>) => {
    setFeedback("");
    if (next === "payment") {
      // Start with what is pending: the date reservation fee first, then the service balance.
      const feeDue = fee > feePaid;
      setPayKind(feeDue ? "reservation_fee" : "payment");
      setPayAmount(String(feeDue ? fee - feePaid : balance || ""));
      setPayMethod("cash");
      setPayDate(mexicoDateKey());
      setPayNote("");
    }
    setAction(next);
  };
  useEffect(() => {
    if (payRequest > 0 && hasPermission("bookings.record_payment")) open("payment");
    // Only a new request opens it, not data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payRequest]);
  const submitPayment = (event: FormEvent) => {
    event.preventDefault();
    const amount = Number(payAmount);
    if (!(amount > 0)) { setFeedbackTone("danger"); setFeedback("Escribe el monto recibido."); return; }
    void run({ type: "booking.record_payment", aggregateId: booking.id, expectedVersion: booking.version, payload: { amount, method: payMethod, kind: payKind, paid_on: payDate, ...(payNote.trim() ? { note: payNote.trim() } : {}) } }, payKind === "reservation_fee" ? "Apartado de fecha registrado." : "Pago registrado.");
  };

  const run = async (request: Parameters<typeof executeCommand>[0], success: string) => {
    setWorking(true);
    setFeedback("");
    try {
      await executeCommand(request);
      setFeedbackTone("success");
      setFeedback(success);
    } catch (caught) {
      setFeedbackTone("danger");
      setFeedback(caught instanceof Error ? caught.message : "No fue posible completar la acción.");
    } finally {
      setWorking(false);
    }
  };

  // Only balloon flights need each passenger (weight balances the basket); school visits and tours do not.
  const withPassengers = booking.service === "balloon";
  const submitEdit = (event: FormEvent) => {
    event.preventDefault();
    const manifest = passengers.map((passenger) => ({
      name: passenger.name.trim(),
      ...(passenger.age ? { age: Number(passenger.age) } : {}),
      ...(passenger.weightKg ? { weight_kg: Number(passenger.weightKg) } : {}),
    }));
    void run({ type: "booking.update_details", aggregateId: booking.id, expectedVersion: booking.version, payload: { customer_name: customer, customer_email: email, customer_phone: phone, language, ...(withPassengers ? { passengers: manifest } : {}) } }, "Datos actualizados y sincronizados con la reserva operativa.");
  };

  const submitAddon = (event: FormEvent) => {
    event.preventDefault();
    if (!alreadyPaid) return;
    if (!addonKey || !addonName || !addonPrice) return;
    void run({ type: "booking.add_paid_addon", aggregateId: booking.id, expectedVersion: booking.version, payload: { name: addonName, quantity: Number(addonQuantity), unit_price: addonPrice, currency: addonCurrency, payment_reference: paymentReference, already_paid: true } }, "Producto pagado agregado. WooCommerce y Stripe no fueron modificados.");
  };

  const share = async (targetLanguage: "es" | "en") => {
    setWorking(true);
    setFeedback("");
    try {
      let result;
      try {
        result = await erpApi.shareConfirmation(booking.id, targetLanguage);
      } catch (caught) {
        if (!(caught instanceof ApiError) || caught.status !== 404) throw caught;
        setFeedbackTone("info");
        setFeedback("Preparando la confirmación para compartir…");
        await executeCommand({
          type: "booking.prepare_confirmation",
          aggregateId: booking.id,
          expectedVersion: booking.version,
          payload: { reason: "Enlace solicitado desde la reserva" },
        });
        result = await waitForShareLink(booking.id, targetLanguage);
      }
      await navigator.clipboard.writeText(result.message);
      setFeedbackTone("success");
      setFeedback(targetLanguage === "es" ? "Mensaje en español copiado." : "English message copied.");
    } catch (caught) {
      setFeedbackTone("danger");
      setFeedback(caught instanceof Error ? caught.message : "No fue posible crear el enlace.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <div className="booking-actions" aria-label={`Acciones de ${booking.reference}`}>
        {!onlyPayment && hasPermission("confirmations.send") ? <ActionButton icon={Mail} label="Enviar correo" disabled={!canConfirm} onClick={() => open("email")} /> : null}
        {hasPermission("bookings.record_payment") ? <ActionButton icon={Wallet} label="Registrar pago" onClick={() => open("payment")} /> : null}
        {onlyPayment ? null : <>
        {hasPermission("bookings.edit") && !hide.includes("edit") ? <ActionButton icon={Pencil} label="Editar reserva" onClick={() => open("edit")} /> : null}
        {hasPermission("bookings.add_paid_addon") && !hide.includes("addon") ? <ActionButton icon={Plus} label="Agregar producto adicional pagado" disabled={!booking.paid} onClick={() => open("addon")} /> : null}
        {hasPermission("confirmations.share") ? <ActionButton icon={Link2} label="Compartir por WhatsApp" onClick={() => open("share")} /> : null}
        {hasPermission("bookings.delete") ? <ActionButton icon={Trash2} label="Eliminar permanentemente" danger onClick={() => open("delete")} /> : null}
        </>}
      </div>

      <Dialog open={Boolean(action)} size={action === "edit" ? "medium" : "compact"} title={action ? actionTitles[action] : "Acción"} eyebrow={booking.reference} onClose={() => setAction(null)}>
        {action === "email" ? (
          <div className="booking-action-form">
            <p>Se generará internamente la confirmación vigente y se enviará a <strong>{booking.email}</strong>.</p>
            {!canConfirm ? <InlineNotice tone="warning">La reserva debe estar confirmada y pagada.</InlineNotice> : null}
            <Button icon={Send} disabled={working || !canConfirm} onClick={() => void run({ type: "booking.send_confirmation", aggregateId: booking.id, expectedVersion: booking.version, payload: { reason: "Envío solicitado desde la reserva" } }, "Correo enviado y estado actualizado.")}>Enviar confirmación</Button>
          </div>
        ) : null}

        {action === "payment" ? (
          <form className="booking-action-form" onSubmit={submitPayment}>
            <p>Registra el dinero que recibiste en efectivo o por transferencia. {balance > 0 ? `Saldo pendiente: $${balance.toLocaleString("es-MX", { minimumFractionDigits: 2 })}.` : ""}</p>
            <SelectField label="¿Qué se pagó?" value={payKind} onChange={(event) => setPayKind(event.target.value as "payment" | "reservation_fee")}>
              <option value="payment">Pago del servicio (anticipo o liquidación)</option>
              <option value="reservation_fee">Apartado de fecha (se descuenta del total)</option>
            </SelectField>
            <div className="booking-action-form__split">
              <Field label="Monto recibido" type="number" min="1" step="0.01" value={payAmount} onChange={(event) => setPayAmount(event.target.value)} required />
              <SelectField label="Cómo" value={payMethod} onChange={(event) => setPayMethod(event.target.value as PaymentMethod)}>
                <option value="cash">Efectivo</option>
                <option value="transfer">Transferencia</option>
                <option value="card">Tarjeta (cobrada por fuera)</option>
              </SelectField>
            </div>
            <DateBox label="Fecha del pago" name="paid_on" value={payDate} onChange={setPayDate} />
            <Field label="Nota (opcional)" value={payNote} maxLength={300} onChange={(event) => setPayNote(event.target.value)} placeholder="Ejemplo: lo entregó la maestra en la visita" />
            <Button type="submit" icon={Wallet} disabled={working}>Registrar pago</Button>
          </form>
        ) : null}

        {action === "edit" ? (
          <form className="booking-action-form" onSubmit={submitEdit}>
            <Field label="Cliente" value={customer} onChange={(event) => setCustomer(event.target.value)} required />
            <Field label="Correo" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            <Field label="Teléfono internacional" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+525512345678" required />
            <SelectField label="Idioma" value={language} onChange={(event) => setLanguage(event.target.value as "es" | "en")}><option value="es">Español</option><option value="en">English</option></SelectField>
            {withPassengers && <div className="passenger-edit-list">
              <h3>Pasajeros</h3>
              {passengers.map((passenger, index) => (
                <fieldset key={index}><legend>Pasajero {index + 1}</legend>
                  <Field label="Nombre completo" value={passenger.name} onChange={(event) => setPassengers((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} required />
                  <div className="booking-action-form__split">
                    <Field label="Edad" type="number" min="0" max="120" value={passenger.age} onChange={(event) => setPassengers((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, age: event.target.value } : item))} required={booking.service === "balloon"} />
                    <Field label="Peso (kg)" type="number" min="1" max="300" step="0.1" value={passenger.weightKg} onChange={(event) => setPassengers((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, weightKg: event.target.value } : item))} required={booking.service === "balloon"} />
                  </div>
                </fieldset>
              ))}
            </div>}
            <Button type="submit" icon={Check} disabled={working}>Guardar cambios</Button>
          </form>
        ) : null}

        {action === "addon" ? (
          <form className="booking-action-form" onSubmit={submitAddon}>
            <InlineNotice tone="warning">Registra aquí únicamente productos que ya fueron pagados fuera del ERP.</InlineNotice>
            <SelectField label="Producto adicional" value={addonKey} disabled={addonCatalogLoading || Boolean(addonCatalogError)} onChange={(event) => {
              const selected = addonCatalog.find((item) => item.key === event.target.value);
              setAddonKey(event.target.value);
              setAddonName(selected?.name ?? "");
              setAddonPrice(selected ? selected.price.toFixed(2) : "");
              setAddonCurrency(selected?.currency ?? "MXN");
              setAddonQuantity(selected?.perPerson ? String(Math.max(1, booking.guests)) : "1");
            }} required>
              <option value="">{addonCatalogLoading ? "Consultando Ruta Mágica..." : "Selecciona un producto"}</option>
              {addonCatalog.map((item) => <option key={item.key} value={item.key}>{item.name} · ${item.price.toLocaleString("es-MX")} {item.currency}</option>)}
            </SelectField>
            {addonCatalogError ? <InlineNotice tone="danger">{addonCatalogError} No se habilitó la captura manual para evitar usar un precio incorrecto.</InlineNotice> : null}
            {addonKey ? <p className="booking-addon-description">{addonCatalog.find((item) => item.key === addonKey)?.description || "Precio vigente del catálogo público de Ruta Mágica."}</p> : null}
            <div className="booking-action-form__split"><Field label="Cantidad" type="number" min="1" max="50" value={addonQuantity} onChange={(event) => setAddonQuantity(event.target.value)} required /><Field label={`Precio unitario (${addonCurrency})`} type="number" value={addonPrice} readOnly required /></div>
            <Field label="Referencia del pago" value={paymentReference} onChange={(event) => setPaymentReference(event.target.value)} hint="Ejemplo: depósito del 29/08 confirmado por Edwin" required />
            <label className="paid-confirmation"><input type="checkbox" checked={alreadyPaid} onChange={(event) => setAlreadyPaid(event.target.checked)} /><span><strong>Confirmo que este producto ya fue pagado</strong><small>No se realizará ningún cargo en WooCommerce ni Stripe.</small></span></label>
            <Button type="submit" icon={Plus} disabled={working || !alreadyPaid || !addonKey || Boolean(addonCatalogError)}>Agregar producto</Button>
          </form>
        ) : null}

        {action === "share" ? (
          <div className="booking-action-form">
            <p>Elige el idioma del mensaje que se copiará para WhatsApp.</p>
            <div className="share-language-actions"><Button icon={Copy} disabled={working} onClick={() => void share("es")}>Español</Button><Button variant="secondary" icon={Copy} disabled={working} onClick={() => void share("en")}>English</Button></div>
          </div>
        ) : null}

        {action === "delete" ? (
          <form className="booking-action-form booking-action-form--danger" onSubmit={(event) => { event.preventDefault(); void run({ type: "booking.delete_permanently", aggregateId: booking.id, expectedVersion: booking.version, payload: { confirmation_reference: deleteReference, reason: deleteReason } }, "Reserva operativa eliminada. El pedido y el historial financiero se conservaron."); }}>
            <InlineNotice tone="danger">Esta acción elimina pasajeros, complementos y operación de la reserva. No elimina el pedido ni su historial financiero.</InlineNotice>
            <Field label={`Escribe ${booking.reference} para confirmar`} value={deleteReference} onChange={(event) => setDeleteReference(event.target.value)} required />
            <Field label="Motivo" value={deleteReason} onChange={(event) => setDeleteReason(event.target.value)} required />
            <Button type="submit" variant="danger" icon={Trash2} disabled={working || deleteReference.toUpperCase() !== booking.reference.toUpperCase() || !deleteReason.trim()}>Eliminar permanentemente</Button>
          </form>
        ) : null}

        {feedback ? <InlineNotice tone={feedbackTone === "neutral" ? "info" : feedbackTone}>{feedback}</InlineNotice> : null}
      </Dialog>
    </>
  );
}

function ActionButton({ icon: Icon, label, disabled = false, danger = false, onClick }: { icon: LucideIcon; label: string; disabled?: boolean; danger?: boolean; onClick: () => void }) {
  return <button type="button" className={`booking-action-button${danger ? " booking-action-button--danger" : ""}`} aria-label={label} title={label} disabled={disabled} onClick={onClick}><Icon aria-hidden="true" /><span>{label}</span></button>;
}

function passengerDrafts(booking: Booking) {
  return Array.from({ length: Math.max(1, booking.guests) }, (_, index) => {
    const passenger = booking.passengers[index];
    return { name: passenger?.name ?? (index === 0 ? booking.customer : ""), age: passenger?.age === undefined ? "" : String(passenger.age), weightKg: passenger?.weightKg === undefined ? "" : String(passenger.weightKg) };
  });
}

async function waitForShareLink(bookingId: string, language: "es" | "en") {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 1_000));
    try {
      return await erpApi.shareConfirmation(bookingId, language);
    } catch (caught) {
      if (!(caught instanceof ApiError) || caught.status !== 404) throw caught;
    }
  }
  throw new Error("La confirmación tardó demasiado en prepararse. Intenta compartirla nuevamente.");
}
